// src/modules/assetManagement/components/AssetModals.tsx
// Link, unlink, share, confirm and first-line support dialogs.
import React, { useMemo, useState } from "react";
import { AlertTriangle, CalendarDays, Check, CheckCircle2, Copy, Headphones, Package } from "lucide-react";
import { assetApi } from "../assetApi";
import type { Asset, LibraryEvent, SupportTicket } from "../types";
import { useMockQuery } from "../useMockQuery";
import { ENTITY } from "../utils";
import { uncoveredProducts, type LinkSelection } from "../linkRules";
import EventProductPicker from "./EventProductPicker";
import { Button, Modal, SearchInput } from "./ui";

// ---- pick list -------------------------------------------------------------

interface PickItem {
  id: string;
  label: string;
  meta?: string;
  /** checked and locked, e.g. already linked to every selected asset */
  locked?: boolean;
}

const PickList: React.FC<{
  title: string;
  icon: React.ReactNode;
  accent: string;
  items: PickItem[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  emptyText: string;
}> = ({ title, icon, accent, items, selected, onToggle, emptyText }) => {
  const [query, setQuery] = useState("");
  const visible = items.filter((i) => i.label.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <div className="flex flex-col min-w-0">
      <div className="flex items-center gap-2 mb-2">
        <span style={{ color: accent }}>{icon}</span>
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        {selected.size > 0 && <span className="text-xs text-slate-500">· {selected.size} selected</span>}
      </div>
      {items.length > 6 && (
        <SearchInput value={query} onChange={setQuery} placeholder={`Search ${title.toLowerCase()}`} className="w-full mb-2" />
      )}
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
        {visible.length === 0 && <p className="text-sm text-slate-500 p-3 text-center">{emptyText}</p>}
        {visible.map((item) => (
          <label
            key={item.id}
            className={`flex items-center gap-3 px-3 py-2.5 text-sm ${item.locked ? "bg-slate-50 cursor-default" : "cursor-pointer hover:bg-slate-50"}`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 accent-slate-700"
              checked={item.locked || selected.has(item.id)}
              disabled={item.locked}
              onChange={() => onToggle(item.id)}
            />
            <span className="flex-1 min-w-0">
              <span className="block truncate text-slate-800">{item.label}</span>
              {item.meta && <span className="block text-xs text-slate-500">{item.meta}</span>}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

const toggleIn = (set: Set<string>, id: string) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

const useLookups = () => {
  const { data: events = [] } = useMockQuery(() => assetApi.listEvents(), []);
  const { data: products = [] } = useMockQuery(() => assetApi.listProducts(), []);
  return { events, products };
};

const countLinked = (assets: Asset[], key: "eventIds" | "productIds", id: string) =>
  assets.filter((a) => a[key].includes(id)).length;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// ---- link ------------------------------------------------------------------

export const LinkModal: React.FC<{
  assets: Asset[];
  onClose: () => void;
  onDone: (message: string) => void;
}> = ({ assets, onClose, onDone }) => {
  const { events, products } = useLookups();
  const [sel, setSel] = useState<LinkSelection>({ eventIds: [], productIds: [] });
  const [saving, setSaving] = useState(false);
  const n = assets.length;

  // Links every selected asset already has count as covering, and show as ticked
  const lockedEventIds = events.filter((e) => countLinked(assets, "eventIds", e.id) === n).map((e) => e.id);
  const lockedProductIds = products.filter((p) => countLinked(assets, "productIds", p.id) === n).map((p) => p.id);
  const metaFor = (key: "eventIds" | "productIds") => (id: string) => {
    const c = countLinked(assets, key, id);
    return c === 0 ? undefined : c === n ? "Already linked" : `Linked to ${c} of ${n} selected`;
  };

  const total = sel.eventIds.length + sel.productIds.length;
  const blocked = uncoveredProducts(events, sel, lockedEventIds).length > 0;

  const save = async () => {
    setSaving(true);
    await assetApi.linkAssets(assets.map((a) => a.id), sel.eventIds, sel.productIds);
    setSaving(false);
    onDone(`Linked ${plural(n, "asset")} to ${plural(total, "item")}`);
  };

  return (
    <Modal
      title="Link to Events or Products"
      subtitle={n === 1 ? assets[0].filename : `${n} assets selected`}
      onClose={onClose}
      width="max-w-3xl"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={total === 0 || blocked} loading={saving}>
            Link{total > 0 ? ` ${plural(total, "item")}` : ""}
          </Button>
        </>
      }
    >
      <EventProductPicker
        events={events}
        products={products}
        value={sel}
        onChange={setSel}
        lockedEventIds={lockedEventIds}
        lockedProductIds={lockedProductIds}
        eventMeta={metaFor("eventIds")}
        productMeta={(id) => metaFor("productIds")(id) ?? inEventsLabel(events, id)}
      />
    </Modal>
  );
};

const inEventsLabel = (events: LibraryEvent[], productId: string) => {
  const names = events.filter((e) => e.productIds.includes(productId)).map((e) => e.title);
  return names.length ? `In ${names.join(", ")}` : undefined;
};

// ---- unlink ----------------------------------------------------------------

export const UnlinkModal: React.FC<{
  assets: Asset[];
  onClose: () => void;
  onDone: (message: string) => void;
}> = ({ assets, onClose, onDone }) => {
  const { events, products } = useLookups();
  const [eventSel, setEventSel] = useState<Set<string>>(new Set());
  const [productSel, setProductSel] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const n = assets.length;

  const linkedEvents = events.filter((e) => countLinked(assets, "eventIds", e.id) > 0);
  const linkedProducts = products.filter((p) => countLinked(assets, "productIds", p.id) > 0);
  const meta = (c: number) => (n === 1 ? undefined : `On ${c} of ${n} selected`);
  const total = eventSel.size + productSel.size;

  const save = async () => {
    setSaving(true);
    await assetApi.unlinkAssets(assets.map((a) => a.id), [...eventSel], [...productSel]);
    setSaving(false);
    onDone(`Removed ${plural(total, "link")} from ${plural(n, "asset")}`);
  };

  return (
    <Modal
      title="Unlink from Events or Products"
      subtitle="Fixes a mistaken link, or tidies up after an Event ends or a Product is removed. The asset itself is kept."
      onClose={onClose}
      width="max-w-3xl"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={save} disabled={total === 0} loading={saving}>
            Unlink{total > 0 ? ` ${plural(total, "item")}` : ""}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <PickList
          title="Events"
          icon={<CalendarDays size={16} />}
          accent={ENTITY.event.solid}
          items={linkedEvents.map((e) => ({ id: e.id, label: e.title, meta: meta(countLinked(assets, "eventIds", e.id)) }))}
          selected={eventSel}
          onToggle={(id) => setEventSel((s) => toggleIn(s, id))}
          emptyText="Not linked to any event"
        />
        <PickList
          title="Products"
          icon={<Package size={16} />}
          accent={ENTITY.product.solid}
          items={linkedProducts.map((p) => ({ id: p.id, label: p.title, meta: meta(countLinked(assets, "productIds", p.id)) }))}
          selected={productSel}
          onToggle={(id) => setProductSel((s) => toggleIn(s, id))}
          emptyText="Not linked to any product"
        />
      </div>
      {eventSel.size > 0 && (
        <p className="mt-4 text-xs text-slate-500">
          Products only reachable through the events you unlink are unlinked too, since a product link needs its event.
        </p>
      )}
    </Modal>
  );
};

// ---- share -----------------------------------------------------------------

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isoDate = (d: Date) => d.toISOString().split("T")[0];

export const ShareModal: React.FC<{
  assets: Asset[];
  onClose: () => void;
  onDone: (message: string) => void;
}> = ({ assets, onClose, onDone }) => {
  const shareable = assets.filter((a) => a.storage === "standard" && a.status === "completed");
  const blocked = assets.filter((a) => !shareable.includes(a));
  const [email, setEmail] = useState("");
  const [expiry, setExpiry] = useState(() => isoDate(new Date(Date.now() + 7 * 864e5)));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const today = isoDate(new Date());

  const send = async () => {
    if (!isValidEmail(email.trim())) return setError("Enter a valid email address.");
    if (!expiry || expiry < today) return setError("Choose an expiry date from today onwards.");
    setError(null);
    setSaving(true);
    const share = await assetApi.shareAssets(shareable.map((a) => a.id), email.trim(), expiry);
    setSaving(false);
    setLink(`${window.location.origin}/guest/shares/${share.id}`);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link ?? "");
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (link) {
    return (
      <Modal
        title="Share sent"
        onClose={() => onDone(`Shared ${plural(shareable.length, "asset")} with ${email.trim()}`)}
        footer={<Button variant="primary" onClick={() => onDone(`Shared ${plural(shareable.length, "asset")} with ${email.trim()}`)}>Done</Button>}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={20} />
          <p className="text-sm text-slate-700">
            <span className="font-medium">{email.trim()}</span> will get an email with access to{" "}
            {plural(shareable.length, "asset")} until {new Date(expiry).toLocaleDateString()}.
          </p>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <input readOnly value={link} className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg bg-slate-50 text-slate-600" />
          <Button onClick={copy} icon={copied ? <Check size={14} /> : <Copy size={14} />}>
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Share assets"
      subtitle={`Give a guest view and download access to ${plural(shareable.length, "asset")}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={send} loading={saving} disabled={shareable.length === 0}>
            Send access
          </Button>
        </>
      }
    >
      {blocked.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 flex gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            {plural(blocked.length, "asset")} can't be shared and will be left out: archived, restoring or not ready
            assets have no viewable copy.
            <ul className="mt-1 list-disc pl-4 text-amber-800">
              {blocked.slice(0, 4).map((a) => (
                <li key={a.id} className="truncate">{a.filename}</li>
              ))}
              {blocked.length > 4 && <li>and {blocked.length - 4} more</li>}
            </ul>
          </div>
        </div>
      )}
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Guest email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@agency.com"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Access expires</span>
          <input
            type="date"
            value={expiry}
            min={today}
            onChange={(e) => setExpiry(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
};

// ---- confirm -----------------------------------------------------------------

export const ConfirmModal: React.FC<{
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  onConfirm: () => Promise<void>;
  onClose: () => void;
}> = ({ title, body, confirmLabel, tone = "primary", onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={tone}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              await onConfirm();
              setBusy(false);
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-slate-700 space-y-3">{body}</div>
    </Modal>
  );
};

// ---- first-line support ------------------------------------------------------

export const SUPPORT_CATEGORIES = [
  "Upload failed",
  "Processing or playback problem",
  "Wrong details or links",
  "Archive or restore",
  "Sharing or guest access",
  "Something else",
];

export const SupportModal: React.FC<{
  asset?: Asset | null;
  fileName?: string;
  defaultCategory?: string;
  defaultDescription?: string;
  onClose: () => void;
  onRaised?: (ticket: SupportTicket) => void;
}> = ({ asset, fileName, defaultCategory, defaultDescription, onClose, onRaised }) => {
  const name = asset?.filename ?? fileName ?? "";
  const initialCategory = useMemo(() => {
    if (defaultCategory) return defaultCategory;
    if (asset?.status === "failed" || asset?.status === "rejected") return "Processing or playback problem";
    if (asset && asset.storage !== "standard") return "Archive or restore";
    return SUPPORT_CATEGORIES[0];
  }, [asset, defaultCategory]);
  const [category, setCategory] = useState(initialCategory);
  const [priority, setPriority] = useState<SupportTicket["priority"]>(
    asset?.status === "failed" || defaultCategory === "Upload failed" ? "HIGH" : "MEDIUM",
  );
  const [description, setDescription] = useState(
    defaultDescription ?? (asset?.errorMessage ? `System message: ${asset.errorMessage}\n\n` : ""),
  );
  const [saving, setSaving] = useState(false);
  const [ticket, setTicket] = useState<SupportTicket | null>(null);

  const submit = async () => {
    setSaving(true);
    const t = await assetApi.raiseSupportTicket({
      assetId: asset?.id ?? null,
      fileName: name,
      category,
      priority,
      description: description.trim(),
    });
    setSaving(false);
    setTicket(t);
    onRaised?.(t);
  };

  if (ticket) {
    return (
      <Modal title="Support request sent" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-slate-700">
            <p>
              Ticket <span className="font-semibold">{ticket.ref}</span> was raised for <span className="font-medium">{name}</span>.
            </p>
            <p className="mt-1 text-slate-500">First-line support replies within one business day. You can follow it under Tickets.</p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Get first-line support"
      subtitle={
        <span className="inline-flex items-center gap-1.5">
          <Headphones size={14} /> {name}
        </span>
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving} disabled={description.trim().length < 5}>
            Send to support
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block sm:col-span-2">
            <span className="text-sm font-medium text-slate-700">What's the issue?</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
            >
              {SUPPORT_CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Priority</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as SupportTicket["priority"])}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>
          </label>
        </div>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Details</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder="What happened, and what did you expect?"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
        </label>
        <p className="text-xs text-slate-500">The asset's ID, status and links are attached automatically.</p>
      </div>
    </Modal>
  );
};
